import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { workingDayService } from '@/api/domains/workingDay';
import { workingDayTypeCatalogService } from '@/api/domains/catalog/workingDayType';
import type { WorkingDayType } from '@/api/domains/catalog/workingDayType';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { workingDayFormSchema, type WorkingDayFormValues } from '@/schemas/schedule/workingDayForm.schema';

// ── Record type (received when opening panel in edit mode) ─────────────────

export interface WorkingDayFormRecord {
  id: number;
  name: string;
  workingDays: number | null;
  offDays: number | null;
  workingDayTypeName: string;
}

// ── Hook params ────────────────────────────────────────────────────────────

interface UseWorkingDayFormPanelParams {
  onSubmit?: () => void;
  readOnly?: boolean;
  record?: WorkingDayFormRecord;
}

// ── Return type ────────────────────────────────────────────────────────────

export interface UseWorkingDayFormPanelResult {
  nameValue: string;
  workingDaysValue: string;
  offDaysValue: string;
  workingDayTypeValue: string;
  workingDayTypeOptions: { value: string; label: string }[];
  isEditMode: boolean;
  effectiveReadOnly: boolean;
  isSubmitting: boolean;
  isReady: boolean;
  canSave: boolean;
  errors: ReturnType<typeof useForm<WorkingDayFormValues>>['formState']['errors'];
  isSubmitted: boolean;
  handleNameChange: (value: string) => void;
  handleWorkingDaysChange: (value: string) => void;
  handleOffDaysChange: (value: string) => void;
  handleWorkingDayTypeChange: (value: string | string[]) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useWorkingDayFormPanel({
  onSubmit,
  readOnly,
  record,
}: UseWorkingDayFormPanelParams): UseWorkingDayFormPanelResult {
  const { t: translate } = useTranslation();
  const { showSuccess, showApiError, showError, closeSidePanel } = useUI();
  const { setLoading } = useSidePanelLoading();

  const isEditMode = !!record?.id;
  const effectiveReadOnly = !!readOnly;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // ── Local field state ──────────────────────────────────────────────────
  const [nameValue, setNameValue] = useState<string>('');
  const [workingDaysValue, setWorkingDaysValue] = useState<string>('');
  const [offDaysValue, setOffDaysValue] = useState<string>('');
  const [workingDayTypeValue, setWorkingDayTypeValue] = useState<string>('');

  // ── Catalog loading ────────────────────────────────────────────────────
  const workingDayTypeCatalog = useCatalogLoader<WorkingDayType>(
    (signal) => workingDayTypeCatalogService.getWorkingDayTypes(signal),
    (item) => ({ value: String(item.id), label: item.description }),
    [],
    false,
    () => showError(translate('schedule:workingDay.messages.loadError')),
  );

  useEffect(() => {
    setLoading(!workingDayTypeCatalog.isLoaded);
  }, [workingDayTypeCatalog.isLoaded, setLoading]);

  // ── Pre-fill in edit mode ──────────────────────────────────────────────
  useEffect(() => {
    if (record) {
      setNameValue(record.name);
      setWorkingDaysValue(record.workingDays != null ? String(record.workingDays) : '');
      setOffDaysValue(record.offDays != null ? String(record.offDays) : '');
    }
  }, [record]);

  // Resolve workingDayType ID from catalog once catalog is ready
  useEffect(() => {
    if (record && workingDayTypeCatalog.isLoaded) {
      const matched = workingDayTypeCatalog.data.find(
        (item) => item.description === record.workingDayTypeName,
      );
      setWorkingDayTypeValue(matched ? String(matched.id) : '');
    }
  }, [record, workingDayTypeCatalog.isLoaded, workingDayTypeCatalog.data]);

  // ── React Hook Form ────────────────────────────────────────────────────
  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    setError,
    register,
    unregister,
    formState: { errors, isSubmitted },
  } = useForm<WorkingDayFormValues>({
    resolver: zodResolver(workingDayFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      workingDays: 0,
      offDays: 0,
      workingDayType: 0,
    },
  });

  // Register fields manually (local-state-driven pattern)
  useEffect(() => {
    register('name');
    register('workingDays');
    register('offDays');
    register('workingDayType');
    return () => {
      unregister('name');
      unregister('workingDays');
      unregister('offDays');
      unregister('workingDayType');
    };
  }, [register, unregister]);

  // Sync local state → form values
  useEffect(() => { setValue('name', nameValue); }, [nameValue, setValue]);
  useEffect(() => { setValue('workingDays', Number(workingDaysValue) || 0); }, [workingDaysValue, setValue]);
  useEffect(() => { setValue('offDays', Number(offDaysValue) || 0); }, [offDaysValue, setValue]);
  useEffect(() => { setValue('workingDayType', Number(workingDayTypeValue) || 0); }, [workingDayTypeValue, setValue]);

  // ── Computed ───────────────────────────────────────────────────────────
  const isReady = useMemo(
    () =>
      workingDayTypeCatalog.isLoaded &&
      nameValue.trim().length > 0 &&
      Number(workingDaysValue) >= 1 &&
      Number(offDaysValue) >= 0 &&
      Number(workingDayTypeValue) > 0,
    [workingDayTypeCatalog.isLoaded, 
      nameValue, 
      workingDaysValue, 
      offDaysValue, 
      workingDayTypeValue],
  );

  const canSave = isReady && !effectiveReadOnly;

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleNameChange = useCallback((value: string) => {
    setNameValue(value);
  }, []);

  const handleWorkingDaysChange = useCallback((value: string) => {
    setWorkingDaysValue(value);
  }, []);

  const handleOffDaysChange = useCallback((value: string) => {
    setOffDaysValue(value);
  }, []);

  const handleWorkingDayTypeChange = useCallback((value: string | string[]) => {
    setWorkingDayTypeValue(Array.isArray(value) ? (value[0] ?? '') : value);
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────
  const onValidSubmit = useCallback(
    async (values: WorkingDayFormValues) => {
      setIsSubmitting(true);
      try {
        const payload = {
          name: values.name,
          workingDays: values.workingDays,
          offDays: values.offDays,
          workingDayType: values.workingDayType,
        };

        const response = isEditMode && record
          ? await workingDayService.update(record.id, payload)
          : await workingDayService.create(payload);

        if (!response.success) {
          if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
            injectValidationErrors(response.error.validationErrors, { setError });
            return;
          }
          showApiError(
            response,
            translate(
              isEditMode
                ? 'schedule:workingDay.messages.updateError'
                : 'schedule:workingDay.messages.createError',
            ),
          );
          return;
        }

        showSuccess(
          translate(
            isEditMode
              ? 'schedule:workingDay.messages.updateSuccess'
              : 'schedule:workingDay.messages.createSuccess',
          ),
        );
        closeSidePanel();
        onSubmit?.();
      } finally {
        setIsSubmitting(false);
      }
    },
    [isEditMode, record, translate, setError, showApiError, showSuccess, closeSidePanel, onSubmit],
  );

  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      void rhfHandleSubmit(onValidSubmit)(event as unknown as React.BaseSyntheticEvent);
    },
    [rhfHandleSubmit, onValidSubmit],
  );

  return {
    nameValue,
    workingDaysValue,
    offDaysValue,
    workingDayTypeValue,
    workingDayTypeOptions: workingDayTypeCatalog.options,
    isEditMode,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    canSave,
    errors,
    isSubmitted,
    handleNameChange,
    handleWorkingDaysChange,
    handleOffDaysChange,
    handleWorkingDayTypeChange,
    handleSubmit,
  };
}
